import React from 'react';
import { FeatureFlags } from '@fabric-ui/components';
import { WarningFullOrange as Warning } from '@fabric-ui/icons/colorful';
import {
  Bars,
  Donut,
  HorizontalBars,
  HorizontalBarsStacked,
  HorizontalBarsStacked100,
  Line,
  Line2,
  Pie,
  PivotTableEmpty as PivotTable,
  StatusBar,
  Table,
  VerticalBarsStacked,
  VerticalBarsStacked100,
} from '@fabric-ui/icons/widgetTypes/noColors';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { useAvContext } from '../../../../context/AvContextProvider';
import { BarSubType, LineSubType, TileSubType } from '../../types';
import { PieSubType } from '../../types/PieWidget.types';
import { TableSubType } from '../../types/TableWidget.types';

const DimsText = ({ dimMin, dimMax, dimType = 'Dimensions' }: { dimMin: number; dimMax: number; dimType?: string }) =>
  !(dimMin === 0 && dimMax === 0) && (
    <b>
      {dimMin === dimMax ? dimMax : `${dimMin} - ${dimMax}`} {dimType} and{' '}
    </b>
  );

const style = iconSize('40%' as any);
interface Props {
  icon: React.JSX.Element;
  dimMin?: number;
  dimMax?: number;
  metricsMin?: number;
  metricsMax?: number;
  splitDimsCount?: boolean;
  colDimMin?: number;
  colDimMax?: number;
  hideText?: boolean;
}

const Preview: React.FC<Props> = ({
  icon,
  dimMin = 0,
  dimMax = 5,
  metricsMin = 0,
  metricsMax = 5,
  splitDimsCount = false,
  colDimMin = 0,
  colDimMax = 5,
  hideText = false,
}) => (
  <Box sx={{ ...flex.colCenter, flexGrow: 1, gap: 2.5 }}>
    {icon}
    <Box sx={{ ...flex.colCenter, gap: 2 }}>
      <Box sx={{ fontSize: 24, fontWeight: 600, color: theme => theme.palette.colors.neutrals[600] }}>Start building your widget</Box>
      {!hideText && (
        <Box sx={{ ...flex.center, gap: 1 }}>
          <Warning />
          <Box sx={{ fontSize: 13, color: theme => theme.palette.colors.neutrals[600] }}>
            Add{' '}
            {splitDimsCount ? (
              <>
                <DimsText dimMin={dimMin} dimMax={dimMax} dimType="Row Dimension" />
                <DimsText dimMin={colDimMin} dimMax={colDimMax} dimType="Column Dimension" />
              </>
            ) : (
              <DimsText dimMin={dimMin} dimMax={dimMax} />
            )}
            <b>{metricsMin === metricsMax ? metricsMax : `${metricsMin} - ${metricsMax}`} Measurements</b>
          </Box>
        </Box>
      )}
    </Box>
  </Box>
);

const RenderPreview = ({ type }) => {
  const { featureFlags } = useAvContext();

  return {
    [TableSubType.Table]: <Preview icon={<Table style={style} />} hideText={featureFlags[FeatureFlags.CDOverSelection]} />,
    [TableSubType.PivotTable]: (
      <Preview
        icon={<PivotTable style={style} />}
        splitDimsCount
        dimMin={1}
        dimMax={1}
        colDimMin={1}
        colDimMax={1}
        metricsMax={5}
        metricsMin={0}
      />
    ),
    [PieSubType.Pie]: <Preview icon={<Pie style={style} />} dimMin={0} dimMax={1} metricsMax={1} metricsMin={1} />,
    [PieSubType.Donut]: <Preview icon={<Donut style={style} />} dimMin={0} dimMax={1} metricsMax={1} metricsMin={0} />,
    [BarSubType.BarVertical]: <Preview icon={<Bars style={style} />} dimMin={1} dimMax={2} metricsMax={5} metricsMin={1} />,
    [BarSubType.BarHorizontal]: <Preview icon={<HorizontalBars style={style} />} dimMin={1} dimMax={2} metricsMax={5} metricsMin={1} />,
    [BarSubType.StackedVertical]: (
      <Preview icon={<VerticalBarsStacked style={style} />} dimMin={1} dimMax={2} metricsMax={5} metricsMin={1} />
    ),
    [BarSubType.StackedHorizontal]: (
      <Preview icon={<HorizontalBarsStacked style={style} />} dimMin={1} dimMax={2} metricsMax={5} metricsMin={1} />
    ),
    [BarSubType.Percentage100_Vertical]: (
      <Preview icon={<VerticalBarsStacked100 style={style} />} dimMin={1} dimMax={2} metricsMax={5} metricsMin={1} />
    ),
    [BarSubType.Percentage100_Horizontal]: (
      <Preview icon={<HorizontalBarsStacked100 style={style} />} dimMin={1} dimMax={2} metricsMax={5} metricsMin={1} />
    ),
    [LineSubType.Line]: <Preview icon={<Line style={style} />} dimMin={1} dimMax={1} metricsMin={1} />,
    [LineSubType.Spline]: <Preview icon={<Line2 style={style} />} dimMin={1} dimMax={1} metricsMin={1} />,
    [TileSubType.Trend]: <Preview icon={<StatusBar style={style} />} dimMin={0} dimMax={0} metricsMin={1} metricsMax={1} />,
    [TileSubType.Tile]: <Preview icon={<StatusBar style={style} />} dimMin={0} dimMax={0} metricsMin={1} metricsMax={1} />,
  }[type];
};

export default RenderPreview;
